import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { practiceSettingsPath } from "../../../../shared/navigation/practiceSettings";
import {
    OnlinePlatform,
    SchedulerSettings,
    SessionUbication,
    TherapistSpace,
} from "../../domain/types";

interface SessionTypeSelectorProps {
    ubication: SessionUbication;
    onUbicationChange: (ubication: SessionUbication) => void;
    platform: OnlinePlatform | null;
    onPlatformChange: (platform: OnlinePlatform | null) => void;
    spaceId: string | null;
    onSpaceChange: (spaceId: string | null) => void;
    settings: SchedulerSettings;
    spaces: TherapistSpace[];
    /** Whether the therapist has adopted AlchelyMeeting. Off by default: this
     *  component stays presentational, and the caller owns the query. */
    alchelyMeetingAvailable?: boolean;
}

/**
 * Location picker. A platform whose integration is not connected is offered as
 * disabled rather than hidden, so the therapist can see why it is unavailable.
 */
export default function SessionTypeSelector({
    ubication,
    onUbicationChange,
    platform,
    onPlatformChange,
    spaceId,
    onSpaceChange,
    settings,
    spaces,
    alchelyMeetingAvailable = false,
}: SessionTypeSelectorProps) {
    const { t } = useTranslation();

    // Ours leads: it is the only one that is always available, and the only
    // one where the patient never leaves Alchely to attend.
    const platforms: { value: OnlinePlatform; connected: boolean }[] = [
        ...(alchelyMeetingAvailable
            ? [{ value: OnlinePlatform.ALCHELY, connected: true }]
            : []),
        { value: OnlinePlatform.GOOGLE_MEET, connected: settings.google_connected },
        { value: OnlinePlatform.ZOOM, connected: settings.zoom_connected },
        {
            value: OnlinePlatform.MICROSOFT_TEAMS,
            connected: settings.microsoft_connected,
        },
    ];

    /**
     * Two mutually exclusive choices, as two segments rather than two native
     * radios. The radios were unstyled browser defaults — a platform-blue dot in
     * an Alchely form — and the hit target was the 13px circle itself, under
     * the 24px WCAG 2.5.8 asks for.
     *
     * The input stays in the DOM and stays focusable; it is only visually
     * hidden, so this is still a radiogroup to a screen reader and arrow keys
     * still move between the two. `has-[:focus-visible]` puts the ring on the
     * label, which is what is actually on screen.
     */
    const ubicationTab = (value: SessionUbication, text: string) => (
        <label
            className={`flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors duration-[--duration-fast] has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent-text ${
                ubication === value
                    ? "border-accent bg-accent-tint text-accent-on-tint"
                    : "border-border-strong text-content-secondary hover:border-accent hover:text-content-primary"
            }`}
        >
            <input
                type="radio"
                name="ubication"
                className="sr-only"
                checked={ubication === value}
                onChange={() => onUbicationChange(value)}
            />
            {text}
        </label>
    );

    return (
        <fieldset className="flex flex-col gap-3">
            <legend className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
                {t("scheduler.schedule.type")}
            </legend>

            <div className="flex gap-2">
                {ubicationTab(SessionUbication.ONLINE, t("scheduler.schedule.online"))}
                {ubicationTab(SessionUbication.IN_PERSON, t("scheduler.schedule.inPerson"))}
            </div>

            {ubication === SessionUbication.ONLINE ? (
                <label className="flex flex-col gap-1.5 text-sm font-medium text-content-secondary">
                    {t("scheduler.schedule.platform")}
                    <select
                        aria-label={t("scheduler.schedule.platform")}
                        value={platform ?? ""}
                        onChange={(event) =>
                            onPlatformChange(
                                event.target.value
                                    ? (event.target.value as OnlinePlatform)
                                    : null
                            )
                        }
                        className="field-base field-default field-height"
                    >
                        <option value="">
                            {t("scheduler.schedule.noPlatform")}
                        </option>
                        {platforms.map(({ value, connected }) => (
                            <option key={value} value={value} disabled={!connected}>
                                {t(`scheduler.platforms.${value}`)}
                                {connected ? "" : ` — ${t("scheduler.schedule.notConnected")}`}
                            </option>
                        ))}
                    </select>
                    {!settings.google_connected &&
                        !settings.zoom_connected &&
                        !settings.microsoft_connected && (
                        // An online session with no connected platform can
                        // never carry a link, and nothing later can add one.
                        <span className="text-xs font-normal text-content-secondary">
                            {t("scheduler.schedule.noPlatformConnected")}{" "}
                            <Link
                                to={practiceSettingsPath("scheduler")}
                                className="font-medium text-accent-text underline underline-offset-2"
                            >
                                {t("scheduler.schedule.connectPlatform")}
                            </Link>
                        </span>
                    )}
                </label>
            ) : (
                <label className="flex flex-col gap-1.5 text-sm font-medium text-content-secondary">
                    {t("scheduler.schedule.space")}
                    <select
                        aria-label={t("scheduler.schedule.space")}
                        value={spaceId ?? ""}
                        onChange={(event) =>
                            onSpaceChange(event.target.value || null)
                        }
                        className="field-base field-default field-height"
                    >
                        <option value="">{t("scheduler.schedule.noSpace")}</option>
                        {spaces.map((space) => (
                            <option key={space.id} value={space.id}>
                                {space.name}
                            </option>
                        ))}
                    </select>
                    {spaces.length === 0 && (
                        <span className="text-xs font-normal text-content-secondary">
                            {t("scheduler.schedule.noSpacesYet")}
                        </span>
                    )}
                </label>
            )}
        </fieldset>
    );
}
